// Executive dashboard admin — edit the report/pull config, view sync health,
// and trigger an out-of-band rollup. Self-gates on exec_dashboard == 'admin'.
// Alert-threshold editing is intentionally minimal here (the alert evaluator
// reads telephony_alerts_config); it can be expanded when B5 lands.

import { useCallback, useEffect, useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useHistory } from 'react-router-dom';

import { getCurrentUser, readUser } from '../firebase';
import { resolveExecRole } from '../exec-dashboard/types';
import type { TelephonyConfig, TelephonySyncState } from '../exec-dashboard/types';
import { fmtWhen } from '../exec-dashboard/constants';
import {
  listSyncState,
  loadTelephonyConfig,
  saveTelephonyConfig,
  syncTelephonyNow,
} from '../exec-dashboard/firebase';

import '../pm/theme.css';

type Access = { kind: 'loading' } | { kind: 'denied' } | { kind: 'ok' };

const card: React.CSSProperties = {
  background: 'var(--rr-paper, #fff)',
  border: '1px solid var(--pm-border, #d8d4cc)',
  borderRadius: 12,
  padding: '16px 18px',
  marginTop: 16,
};
const input: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--pm-border, #d8d4cc)',
  borderRadius: 8,
  fontSize: 14,
  width: '100%',
  boxSizing: 'border-box',
  // Pin background/text + force light color-scheme so native controls don't
  // render as black boxes for users whose OS is in dark mode (app is light-only).
  background: 'var(--pm-bg-input, #fff)',
  color: 'var(--pm-text, #1a1a1a)',
  colorScheme: 'light',
};
const label: React.CSSProperties = { fontSize: 12, color: '#888', margin: '10px 0 4px' };

const ExecDashboardAdmin: React.FC = () => {
  const history = useHistory();
  const [access, setAccess] = useState<Access>({ kind: 'loading' });
  const [cfg, setCfg] = useState<Partial<TelephonyConfig>>({});
  const [sync, setSync] = useState<TelephonySyncState[]>([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u: any = await getCurrentUser();
        if (!u) return !cancelled && setAccess({ kind: 'denied' });
        const data = await readUser(u.uid);
        const role = resolveExecRole(data && data.restricted);
        if (!cancelled) setAccess({ kind: role === 'admin' ? 'ok' : 'denied' });
      } catch {
        if (!cancelled) setAccess({ kind: 'denied' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAll = useCallback(async () => {
    const [c, s] = await Promise.all([loadTelephonyConfig().catch(() => null), listSyncState().catch(() => [])]);
    if (c) setCfg(c);
    setSync(s);
  }, []);

  useEffect(() => {
    if (access.kind === 'ok') loadAll();
  }, [access.kind, loadAll]);

  async function save() {
    setBusy(true);
    setMsg('');
    try {
      await saveTelephonyConfig(cfg);
      setMsg('Config saved.');
    } catch (err: any) {
      setMsg(err?.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function syncNow() {
    setBusy(true);
    setMsg('');
    try {
      const res = await syncTelephonyNow();
      setMsg(res?.message || 'Sync triggered.');
      setTimeout(loadAll, 2000);
    } catch (err: any) {
      setMsg(err?.message || 'Sync failed.');
    } finally {
      setBusy(false);
    }
  }

  if (access.kind === 'loading') {
    return (
      <IonPage><IonContent fullscreen><div className="pm-app"><div className="pm-page" style={{ padding: '40px 28px' }}><IonSpinner /></div></div></IonContent></IonPage>
    );
  }
  if (access.kind === 'denied') {
    return (
      <IonPage><IonContent fullscreen><div className="pm-app"><div className="pm-page" style={{ maxWidth: 720, padding: '40px 28px' }}>
        <div className="pm-header pm-header-titles"><h1>Exec dashboard admin</h1><p>Requires the exec_dashboard admin permission.</p></div>
      </div></div></IonContent></IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page" style={{ maxWidth: 900, padding: '24px 28px' }}>
            <div className="pm-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div className="pm-header-titles">
                <h1>Exec dashboard admin</h1>
                <p>Report configuration, sync health, and manual rollup.</p>
              </div>
              <button type="button" className="pm-btn" style={{ padding: '8px 16px', whiteSpace: 'nowrap' }} onClick={() => history.push('/Operator/Exec/Service')}>
                ← Dashboard
              </button>
            </div>

            {msg && <div style={{ marginTop: 8, fontSize: 13 }}>{msg}</div>}

            <div style={card}>
              <h2 style={{ margin: '0 0 4px', fontSize: 16 }}>Report configuration</h2>
              <div style={label}>Reporting timezone (IANA)</div>
              <input style={input} value={cfg.reporting_timezone || ''} onChange={(e) => setCfg({ ...cfg, reporting_timezone: e.target.value })} placeholder="America/Los_Angeles" />
              <div style={label}>Five9 report folder</div>
              <input style={input} value={cfg.report_folder || ''} onChange={(e) => setCfg({ ...cfg, report_folder: e.target.value })} placeholder="RR Exec Dashboard" />
              <div style={label}>Aggregate report name</div>
              <input style={input} value={cfg.aggregate_report_name || ''} onChange={(e) => setCfg({ ...cfg, aggregate_report_name: e.target.value })} placeholder="Exec Campaign Daily" />
              <div style={label}>Detail report name</div>
              <input style={input} value={cfg.detail_report_name || ''} onChange={(e) => setCfg({ ...cfg, detail_report_name: e.target.value })} placeholder="Exec Call Detail" />
              <div style={label}>Backfill days (manual re-pull window)</div>
              <input style={input} type="number" value={cfg.backfill_days ?? 30} onChange={(e) => setCfg({ ...cfg, backfill_days: Number(e.target.value) })} />
              <div style={label}>Excluded campaigns (comma-separated)</div>
              <input
                style={input}
                value={(cfg.excluded_campaigns || []).join(', ')}
                onChange={(e) => setCfg({ ...cfg, excluded_campaigns: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
              />
              <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                <button type="button" className="pm-btn pm-btn-primary" style={{ padding: '8px 16px' }} disabled={busy} onClick={save}>
                  {busy ? '…' : 'Save config'}
                </button>
                <button type="button" className="pm-btn" style={{ padding: '8px 16px' }} disabled={busy} onClick={syncNow}>
                  Sync now
                </button>
              </div>
            </div>

            <div style={card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 16 }}>Sync health</h2>
              {sync.length === 0 && <p style={{ color: '#888', fontSize: 13 }}>No sync runs recorded yet.</p>}
              {sync.map((s) => (
                <div key={s.key} style={{ fontSize: 13, padding: '6px 0', borderTop: '1px solid var(--pm-border, #eee)' }}>
                  <strong>{s.key}</strong> — last run {fmtWhen(s.last_run_at)}
                  {s.last_date_synced ? `, through ${s.last_date_synced}` : ''}
                  {s.rows_scanned != null ? `, ${s.rows_scanned} rows` : ''}
                  {s.last_error ? <span style={{ color: 'var(--rr-red, #C2433D)' }}> — error: {s.last_error}</span> : ' — ok'}
                </div>
              ))}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ExecDashboardAdmin;
